import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { FAQ } from "@/data/faq";

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };

/** Häufige Fragen – zugänglicher Akkordeon-Block. Inhalt = Quelle des FAQPage-Schemas (data/faq.js), damit Sichtbares und Markup übereinstimmen. */
export const Faq = ({ items = FAQ, title = "Häufige Fragen", heading = ["Alles, was du", "wissen willst."], initial, calm = false }) => {
  const [open, setOpen] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const limit = initial && !showAll ? initial : items.length;
  const shown = items.slice(0, limit);
  const hasMore = Boolean(initial) && !showAll && items.length > initial;
  return (
    <section className={calm ? "bg-[#F7F4EE]" : "bg-white"} data-testid="faq-section" aria-labelledby="faq-h">
      <div className={`mx-auto max-w-[840px] px-6 md:px-10 ${calm ? "py-20 md:py-32" : "py-8 md:py-8"}`}>
        <p style={B} className="text-xs font-bold uppercase tracking-[0.18em] text-[#111]/50">{title}</p>
        {calm ? (
          <h2 id="faq-h" style={H} className="mt-3 text-[#111] leading-[1.12] text-[1.7rem] md:text-4xl" data-testid="faq-headline">
            {heading[0]} {heading[1]}
          </h2>
        ) : (
          <h2 id="faq-h" style={H} className="mt-3 text-[#111] uppercase leading-[1.05] text-3xl md:text-4xl" data-testid="faq-headline">
            {heading[0]} <span className="bg-[#FDD105] px-2">{heading[1]}</span>
          </h2>
        )}
        <ul className="mt-5 divide-y divide-[#111]/10 border-y border-[#111]/10">
          {shown.map((item, i) => {
            const on = open === i;
            return (
              <li key={item.q} data-testid={`faq-item-${i}`}>
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpen(on ? -1 : i)}
                    aria-expanded={on}
                    aria-controls={`faq-answer-${i}`}
                    style={B}
                    className="flex w-full items-center justify-between gap-4 py-2.5 text-left text-base md:text-lg font-bold text-[#111] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDD105] focus-visible:ring-offset-2"
                    data-testid={`faq-question-${i}`}>
                    <span className="min-w-0">{item.q}</span>
                    <Plus size={20} className={`shrink-0 text-[#111] transition-transform duration-300 ${on ? "rotate-45" : ""}`} aria-hidden="true" />
                  </button>
                </h3>
                <motion.div
                  id={`faq-answer-${i}`}
                  initial={false}
                  animate={{ height: on ? "auto" : 0, opacity: on ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid={`faq-answer-${i}`}>
                  <p style={B} className="pb-4 pr-8 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{item.a}</p>
                </motion.div>
              </li>
            );
          })}
        </ul>
        {hasMore && (
          <div className="mt-6">
            {calm ? (
              <button type="button" onClick={() => setShowAll(true)} data-testid="faq-show-all" style={B}
                className="inline-flex items-center gap-2 rounded-full border border-[#E5E5E5] bg-white px-6 py-3 text-sm font-semibold text-[#111] transition-colors hover:border-[#111]">
                Alle Fragen <Plus size={16} className="shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <button type="button" onClick={() => setShowAll(true)} data-testid="faq-show-all" style={B}
                className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3 text-sm font-bold text-[#111] shadow-[0_4px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_2px_0_0_#111]">
                Alle Fragen anzeigen <Plus size={16} className="shrink-0" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
